import { Badge } from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, Edit20Regular } from '@fluentui/react-icons';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { ServiceType } from '@mnspro-cloud/services';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { ConfirmationDialogProvider } from '../../../ConfirmationDialog/ConfirmationDialog';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { List, ListEntryProps, ListSelectionType } from '../../../List';
import { CreateServiceTypeDialog } from './Dialogs/CreateServiceTypeDialog';

export interface ServiceTypeConfigurationProps {
  serviceTypes: ServiceType[];
  serviceTypeEdited?: (
    updatedServiceType: ServiceType,
    originalServiceType: ServiceType,
  ) => void;
  serviceTypeDeleted?: (serviceType: ServiceType) => void;
  serviceTypeCreated?: (createdServiceType: ServiceType) => void;
}

const ServiceTypeConfiguration: FC<ServiceTypeConfigurationProps> = props => {
  const {
    serviceTypes,
    serviceTypeEdited,
    serviceTypeDeleted,
    serviceTypeCreated,
  } = props;
  const { getConfirmation } = useConfirmationDialog();
  const { t } = useTranslation();

  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editModel, setEditModel] = useState<ServiceType | null>(null);
  const [originModel, setOriginModel] = useState<ServiceType | null>(null);

  const editButtonClicked = (serviceType: ServiceType) => {
    setEditModel(serviceType);
    setOriginModel(serviceType);
  };
  const handleCancelEditButtonClicked = () => {
    setEditModel(null);
    setOriginModel(null);
  };
  const handleConfirmEditButtonClicked = (editedModel: ServiceType) => {
    serviceTypeEdited?.(editedModel, originModel ?? editedModel);
    setEditModel(null);
    setOriginModel(null);
  };

  const handleCreateButtonClicked = () => setCreateDialogOpen(true);
  const handleCancelCreateButtonClicked = () => setCreateDialogOpen(false);
  const handleConfirmCreateButtonClicked = (
    createdServiceType: ServiceType,
  ) => {
    serviceTypeCreated?.(createdServiceType);
    setCreateDialogOpen(false);
  };

  const deleteButtonClicked = async (serviceType: ServiceType) => {
    const confirmed = await getConfirmation({
      cancelButtonText: t(
        // 'settings:classbook:Service_Type_Confirmation:Dialog_Button_Cancel',
        'Cancel',
        'Abbrechen',
      ),
      confirmButtonText: t('Confirm', { defaultValue: 'Bestätigen' }),
      headerText: t('settings:classbook:Service_Type_Confirmation:Dialog_Header_Text_Delete_Service_Type', { defaultValue: 'Diensttypen löschen' }),
      content: t('settings:classbook:Service_Type_Confirmation:Dialog_Content_Do_you_want_to_delete_service_type', { defaultValue: 'Möchten Sie den Diensttypen löschen' }),
      showCloseButton: false,
    });
    if (confirmed) {
      serviceTypeDeleted?.(serviceType);
    }
  };
  const listItems: ListEntryProps[] = sortBy(serviceTypes, ['fullName']).map(
    (type, index) => {
      return {
        key: `${index}: ${type.id}`,
        label: `${type.fullName} (${type.shortName})`,
        avatar: <Badge color={!type.isActive ? 'danger' : 'success'} size="extra-small" />,
        actions: [
          {
            icon: <Edit20Regular />,
            onClick: () => editButtonClicked(type),
            'data-testid': 'editBtn',
          },
          {
            icon: <Delete20Regular />,
            onClick: () => deleteButtonClicked(type),
            'data-testid': 'deleteBtn',
          },
        ],
      };
    },
  );
  return (
    <Flex
      direction="column"
      gap="0.5rem"
      data-testid="serviceTypeConfigurationWithDialog"
    >
      <Button
        appearance="primary"
        icon={<Add20Regular />}
        onClick={handleCreateButtonClicked}
      >
        {t('Add', { defaultValue: 'Hinzufügen' })}
      </Button>
      <List entries={listItems} selectionType={ListSelectionType.None} />
      {createDialogOpen && (
        <CreateServiceTypeDialog
          isOpen={createDialogOpen}
          cancelled={handleCancelCreateButtonClicked}
          confirmed={handleConfirmCreateButtonClicked}
        />
      )}
      {editModel && (
        <CreateServiceTypeDialog
          editModel={editModel}
          isOpen={true}
          cancelled={handleCancelEditButtonClicked}
          confirmed={handleConfirmEditButtonClicked}
        />
      )}
    </Flex>
  );
};

export const ServiceTypeConfigurationWithDialog: FC<
  ServiceTypeConfigurationProps
> = props => (
  <ConfirmationDialogProvider>
    <ServiceTypeConfiguration {...props} />
  </ConfirmationDialogProvider>
);
